// Fetch-based HTTP client — drop-in replacement for the previous axios-based api.
// Exposes api.get/post/put/delete/patch with the same response shape ({ data, status, headers })
// and the same error shape ({ response: { status, data }, message, config }) so all 89+ callers
// continue to work unchanged.

/**
 * Axios-shaped success response. `headers` is the native fetch `Headers`.
 * Callers narrow `data` themselves (or pass a `T`); default is `unknown`.
 */
export interface ApiResponse<T = unknown> {
  data: T
  status: number
  headers: Headers
}

/**
 * Axios-shaped error. Built ad-hoc throughout this module via `new Error()`
 * then casting to `ApiError` so the extra fields (`response`, `config`,
 * `code`, …) type-check while keeping runtime construction byte-identical.
 */
export interface ApiError extends Error {
  response?: { status: number; data: unknown; headers?: Headers }
  config?: unknown
  code?: string
  cause?: unknown
  // Offline write-guard marker (set by request()).
  isOfflineGuard?: boolean
}

/** Per-request options accepted by the public api.* methods. */
export interface RequestOptions {
  headers?: Record<string, string>
  params?: Record<string, unknown>
  responseType?: 'blob' | 'arrayBuffer' | 'text'
  ignoreSiteFilter?: boolean
  // Legacy axios-compat: a handful of DELETE callers pass a request body as
  // `{ data: {...} }`. The fetch-based client does NOT forward it (DELETE is
  // dispatched with a null body), but the field is accepted so those existing
  // call sites continue to type-check unchanged. Kept for byte-identical
  // runtime behaviour; do not start relying on it for new code.
  data?: unknown
  // Internal retry/step-up flags threaded through recursive request() calls.
  _retry?: boolean
  _stepUpRetry?: boolean
  // Internal counter for the transient-GET retry ladder (UX-03). Counts how
  // many transient retries have already been consumed for this logical request.
  _transientRetryAttempt?: number
}

/** Internal request config echoed back on errors as `err.config`. */
interface RequestConfig {
  method: string
  url: string
  headers: Record<string, string>
  _retry: boolean
  _stepUpRetry?: boolean
}

/** Successful payload returned by POST /api/auth/refresh. */
interface RefreshResponse {
  access_token?: string
  [key: string]: unknown
}

const BASE_URL = '/api'
const TIMEOUT  = 15000
// Refresh runs on a separate, longer budget. The very first request after a
// woken-up tab is exactly the scenario where the network stack is slow; the
// 15s default would abort the refresh, downgrade the error to a network
// failure, and surface a misleading "failed to load data" to the user.
const REFRESH_TIMEOUT = 30000
const REFRESH_NETWORK_RETRY_BACKOFF_MS = 500
// UX-03: transient failures (network error, timeout, HTTP 5xx) on idempotent
// GETs are retried with a short backoff before surfacing to the caller —
// a backend hiccup must never look like a fatal error (or worse, a logout).
// Two retries: 300ms then 900ms.
const GET_TRANSIENT_RETRY_BACKOFFS_MS: readonly number[] = [300, 900]

// Error codes that callers (and React Query error boundaries / QueryErrorState)
// rely on for classification. Keep stable — they are part of the public surface.
export const ERR_REFRESH_AUTH_FAILED    = 'REFRESH_AUTH_FAILED'    // refresh token dead → sign in again
export const ERR_REFRESH_NETWORK_FAILED = 'REFRESH_NETWORK_FAILED' // transient network → retryable
export const ERR_NETWORK                = 'ERR_NETWORK'            // generic fetch network failure
export const ERR_TIMEOUT                = 'ECONNABORTED'           // request timed out (matches axios convention)

let accessToken: string | null | undefined = null
let accessTokenIssuedAt = 0     // ms epoch; updated by setAccessToken
let _authLoading        = true
let _sessionExpiredHandler: ((isPlatform: boolean, reason: string | null) => void) | null = null
let _stepUpHandler: ((arg: { scope: string | null }) => Promise<unknown>) | null = null

/**
 * Register a step-up handler. Invoked when a request fails with 401 +
 * `error_code === 'STEP_UP_REQUIRED'`. The handler is called with `{ scope }`
 * and must resolve once the user has completed step-up reauthentication
 * (POST /api/auth/step-up). The original request is then replayed once.
 *
 * Reject the returned promise to abort retry and propagate the original
 * 401 to the caller.
 */
export function setStepUpHandler(fn: ((arg: { scope: string | null }) => Promise<unknown>) | null): void {
  _stepUpHandler = fn
}

// ── Site filter auto-injection ────────────────────────────────
interface SiteFilterStore {
  getState: () => { siteId?: string | null } | undefined
}
let _siteFilterStore: SiteFilterStore | null = null

/**
 * Register the Zustand siteFilterStore so api.js can auto-inject site_id.
 * Call once at app boot: registerSiteFilterStore(useSiteFilterStore)
 */
export function registerSiteFilterStore(store: SiteFilterStore | null): void {
  _siteFilterStore = store
}

const SITE_FILTER_PREFIXES: readonly string[] = [
  '/items', '/productions', '/users', '/locations', '/bundles', '/spaces',
  '/events', '/loans', '/budgets', '/maintenance', '/forecast', '/insurance',
  '/exports', '/templates', '/search', '/checkouts', '/allocations',
  '/crew', '/season', '/hours', '/qr', '/share', '/kiosk-displays',
  '/data-export', '/greenbook', '/manuscripts', '/rehearsal', '/show-reports',
  '/financial', '/financial-query', '/dashboard',
]

function isWhitelisted(url: string): boolean {
  return SITE_FILTER_PREFIXES.some(p => url === p || url.startsWith(p + '/') || url.startsWith(p + '?'))
}

function getActiveSiteId(): string | null {
  return _siteFilterStore?.getState()?.siteId || null
}

/**
 * Register a callback to be invoked when the session expires and the user
 * needs to be redirected to the login page.  Call this once during app
 * initialisation (e.g. in authStore).  If no handler is registered the
 * previous hard-coded window.location.href behaviour is used as a fallback.
 *
 * @param {(isPlatform: boolean) => void} fn
 */
export function setSessionExpiredHandler(fn: ((isPlatform: boolean, reason: string | null) => void) | null): void {
  _sessionExpiredHandler = fn
}

function _navigateToLogin(isPlatform: boolean, reason: string | null): void {
  if (_sessionExpiredHandler) {
    _sessionExpiredHandler(isPlatform, reason)
    return
  }
  // Fallback: hard navigation (backward-compat)
  const loginPath = isPlatform ? '/platform-login' : '/login'
  window.location.href = reason ? `${loginPath}?reason=${reason}` : loginPath
}

export function setAccessToken(token: string | null | undefined): void {
  accessToken = token
  accessTokenIssuedAt = token ? Date.now() : 0
}
export function getAccessToken(): string | null | undefined       { return accessToken }
export function getAccessTokenAgeMs(): number  { return accessTokenIssuedAt ? Date.now() - accessTokenIssuedAt : Infinity }
export function setAuthLoading(loading: boolean): void { _authLoading = loading }

// ── Shared token refresh (deduped + classified + auto-retry on network) ──
//
// Why this is more than a thin fetch wrapper:
//   • The previous version threw a generic Error on ANY failure, so a transient
//     network blip (slow waking tab) was indistinguishable from a real session
//     death (refresh token revoked). The 401 handler then logged the user out
//     in both cases — the "failed to load data" UX bug on returning to an
//     idle tab.
//   • We now classify failures into REFRESH_AUTH_FAILED (logout the user) vs
//     REFRESH_NETWORK_FAILED (transient, retryable, do NOT logout) and only
//     clear the access token when the failure is truly authoritative.
//   • Network-class failures get one automatic retry with a short backoff,
//     because the first request on a freshly-woken tab disproportionately
//     fails for purely network reasons (cold DNS, TCP slow-start, etc.).
//   • Refresh runs on its own longer timeout (30s, no shared AbortController
//     with regular requests) because aborting refresh is exactly what creates
//     the bug we are fixing.

let refreshPromise: Promise<RefreshResponse> | null = null

function classifyRefreshError(err: unknown): string {
  // err.response is only set when the server returned an HTTP response (set
  // below in _doRefreshFetch). 401/403 → authoritative auth failure.
  const status = (err as ApiError | undefined)?.response?.status
  if (status === 401 || status === 403) return ERR_REFRESH_AUTH_FAILED
  // Anything else (no response, AbortError, fetch TypeError, 5xx) is treated
  // as network-class. 5xx during refresh is rare but we'd rather retry than
  // sign the user out for a backend hiccup.
  return ERR_REFRESH_NETWORK_FAILED
}

async function _doRefreshFetch(): Promise<RefreshResponse> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT)
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
    })
    if (!res.ok) {
      const data = await res.json().catch(() => null)
      const httpErr = new Error(data?.error || `Refresh failed (${res.status})`) as ApiError
      httpErr.response = { status: res.status, data }
      throw httpErr
    }
    return await res.json()
  } finally {
    clearTimeout(timer)
  }
}

export function refreshAccessToken(): Promise<RefreshResponse> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const data = await _doRefreshFetch()
      setAccessToken(data.access_token)
      return data
    } catch (err) {
      const code = classifyRefreshError(err)
      if (code === ERR_REFRESH_NETWORK_FAILED) {
        // Single retry with a short backoff. Covers the cold-tab-wake scenario
        // where DNS/TCP needs to warm up before the very first request lands.
        await new Promise(r => setTimeout(r, REFRESH_NETWORK_RETRY_BACKOFF_MS))
        try {
          const data = await _doRefreshFetch()
          setAccessToken(data.access_token)
          return data
        } catch (retryErrRaw) {
          const retryErr = retryErrRaw as ApiError
          const retryCode = classifyRefreshError(retryErr)
          if (retryCode === ERR_REFRESH_AUTH_FAILED) {
            // The retry hit an authoritative 401 — refresh token actually dead.
            setAccessToken(null)
            retryErr.code = ERR_REFRESH_AUTH_FAILED
            throw retryErr
          }
          // Still network-class after retry. Do NOT clear the access token —
          // the user is likely just offline; existing UI state should survive
          // and React Query should treat this as a retryable error.
          retryErr.code = ERR_REFRESH_NETWORK_FAILED
          throw retryErr
        }
      }
      // First attempt was an authoritative auth failure — refresh token dead.
      setAccessToken(null)
      ;(err as ApiError).code = ERR_REFRESH_AUTH_FAILED
      throw err
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// ── Core request function ─────────────────────────────────────
async function request<T = unknown>(
  method: string,
  url: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<ApiResponse<T>> {
  // ADR-014 D11 — block writes when offline. Reads still go through and
  // hit the service-worker NetworkFirst cache.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const m = String(method || '').toLowerCase()
    if (m === 'post' || m === 'put' || m === 'patch' || m === 'delete') {
      const err = new Error('offline_write_blocked') as ApiError
      err.code = 'OFFLINE_WRITE_BLOCKED'
      err.isOfflineGuard = true
      throw err
    }
  }
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT)

  const headers: Record<string, string> = { ...options.headers }

  // Auto-attach Bearer token
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

  // Set Content-Type for non-FormData bodies
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }
  // For FormData, let the browser set Content-Type with boundary automatically
  if (body instanceof FormData && headers['Content-Type']) {
    delete headers['Content-Type']
  }

  // Build query string from params (with site_id auto-injection)
  let fullUrl = `${BASE_URL}${url}`
  const resolvedParams: Record<string, unknown> = { ...options.params }
  if (!options.ignoreSiteFilter && !resolvedParams.site_id && isWhitelisted(url)) {
    const siteId = getActiveSiteId()
    if (siteId) resolvedParams.site_id = siteId
  }
  if (Object.keys(resolvedParams).length > 0) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(resolvedParams)) {
      // URLSearchParams.append stringifies its value at runtime; the cast only
      // satisfies the type-checker and changes no runtime behaviour.
      if (v !== undefined && v !== null) qs.append(k, v as string)
    }
    const str = qs.toString()
    if (str) fullUrl += `?${str}`
  }

  const config: RequestConfig = { method, url, headers: { ...headers }, _retry: options._retry === true }

  try {
    const res = await fetch(fullUrl, {
      method,
      headers,
      body: body instanceof FormData ? body : body != null ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal: controller.signal,
    })

    clearTimeout(timer)

    // Parse response body
    let data: unknown
    const ct = res.headers.get('content-type') || ''
    if (options.responseType === 'blob') {
      data = await res.blob()
    } else if (options.responseType === 'arrayBuffer') {
      data = await res.arrayBuffer()
    } else if (options.responseType === 'text') {
      data = await res.text()
    } else if (ct.includes('application/json')) {
      data = await res.json()
    } else if (ct.includes('text/')) {
      data = await res.text()
    } else {
      // Unknown content-type with no explicit responseType: try JSON then text.
      try { data = await res.json() } catch { data = await res.text().catch(() => null) }
    }

    if (!res.ok) {
      // Build an error object matching the shape callers expect (err.response.status, err.response.data)
      const d = data as { error?: string; message?: string } | null | undefined
      const err = new Error(d?.error || d?.message || res.statusText) as ApiError
      err.response = { status: res.status, data, headers: res.headers }
      err.config = config
      throw err
    }

    return { data: data as T, status: res.status, headers: res.headers }
  } catch (errRaw) {
    const err = errRaw as ApiError
    clearTimeout(timer)

    // UX-03: transient-failure retry for idempotent GETs. Network errors,
    // timeouts and 5xx responses get up to two automatic retries with a
    // short backoff (300ms, 900ms) before the error surfaces. Writes are
    // never retried (not idempotent); the auth-refresh endpoint has its own
    // dedicated retry logic above.
    const retryTransientGet = async (finalErr: ApiError): Promise<ApiResponse<T>> => {
      const attempt = options._transientRetryAttempt ?? 0
      if (method === 'GET' && !url.includes('/auth/refresh') && attempt < GET_TRANSIENT_RETRY_BACKOFFS_MS.length) {
        await new Promise(r => setTimeout(r, GET_TRANSIENT_RETRY_BACKOFFS_MS[attempt]))
        return request<T>(method, url, body, { ...options, _transientRetryAttempt: attempt + 1 })
      }
      throw finalErr
    }

    // Handle abort (timeout)
    if (err.name === 'AbortError') {
      const timeoutErr = new Error(`Request timed out after ${TIMEOUT}ms`) as ApiError
      timeoutErr.code = ERR_TIMEOUT
      timeoutErr.config = config
      return retryTransientGet(timeoutErr)
    }

    // Network errors (no response). Tag with a stable code so QueryErrorState
    // and React Query can classify without sniffing message strings.
    if (!err.response) {
      if (!err.code) err.code = ERR_NETWORK
      err.config = config
      return retryTransientGet(err)
    }

    // HTTP 5xx: a backend hiccup is NEVER an auth failure — never touch the
    // access token or navigate to login here. Idempotent GETs get the
    // transient retry ladder; everything else propagates to the caller.
    if (err.response.status >= 500) {
      return retryTransientGet(err)
    }

    // ── 401 handling ─────────────────────────────────────────
    const isRefresh = url.includes('/auth/refresh')

    // Narrow the dynamic error body once for the auth-flow checks below.
    const errData = err.response?.data as { error_code?: string; scope?: string } | null | undefined

    // SESSION_REVOKED: token was explicitly invalidated (role change, password
    // reset, admin force-logout, logout-all). Do NOT attempt refresh — the
    // refresh token is also dead in most revocation scenarios. Route to login
    // with a reason parameter so the page can show an explanatory message.
    if (err.response?.status === 401 &&
        errData?.error_code === 'SESSION_REVOKED') {
      setAccessToken(null)
      if (!_authLoading) {
        const path = window.location.pathname
        const isPublicPage = ['/', '/login', '/platform-login', '/signup', '/forgot-password', '/reset-password'].some(p => path === p || path.startsWith(p + '/'))
        if (!isPublicPage) {
          const isPlatform = path.startsWith('/super-admin')
          _navigateToLogin(isPlatform, 'session_revoked')
        }
      }
      throw err
    }

    // ── 401 STEP_UP_REQUIRED: trigger step-up modal, then replay once ─
    if (err.response?.status === 401 &&
        errData?.error_code === 'STEP_UP_REQUIRED' &&
        !config._stepUpRetry &&
        _stepUpHandler) {
      try {
        await _stepUpHandler({ scope: errData?.scope || null })
      } catch (_stepUpErr) {
        // User dismissed / step-up failed — propagate the original error so
        // the caller sees the same 401 instead of the modal-cancel rejection.
        throw err
      }
      return request<T>(method, url, body, { ...options, _stepUpRetry: true })
    }

    // ── 401 auto-retry with token refresh ─────────────────────
    if (err.response?.status === 401 && !config._retry && !isRefresh) {
      config._retry = true
      try {
        const refreshData = await refreshAccessToken()
        // Retry original request with new token; _retry: true prevents a second
        // retry loop if the refreshed token is also rejected.
        return request<T>(method, url, body, {
          ...options,
          _retry: true,
          headers: { ...options.headers, Authorization: `Bearer ${refreshData.access_token || accessToken}` },
        })
      } catch (refreshErrRaw) {
        const refreshErr = refreshErrRaw as ApiError
        // Classify the refresh failure. Only navigate to login when the
        // refresh token itself was rejected — network/timeout failures keep
        // the user on the page and surface as a retryable network error.
        if (refreshErr?.code === ERR_REFRESH_NETWORK_FAILED) {
          // Re-throw as a network-class error so React Query treats it as
          // retryable and QueryErrorState renders "Connection lost", not
          // "Session expired". Preserve the original message for logs.
          const netErr = new Error(refreshErr.message || 'Network error during token refresh') as ApiError
          netErr.code = ERR_NETWORK
          netErr.cause = refreshErr
          netErr.config = config
          throw netErr
        }

        // Authoritative auth failure (or any other refresh error): route to
        // login so the user can re-authenticate.
        if (!_authLoading) {
          const path = window.location.pathname
          const isPublicPage = ['/', '/login', '/platform-login', '/signup', '/forgot-password', '/reset-password'].some(p => path === p || path.startsWith(p + '/'))
          if (!isPublicPage) {
            const isPlatform = path.startsWith('/super-admin')
            _navigateToLogin(isPlatform, null)
          }
        }
        throw refreshErr
      }
    }

    throw err
  }
}

// ── Public API matching previous axios interface ──────────────
/** Axios-shaped client surface. Each method mirrors the previous interface. */
export interface ApiClient {
  get<T = unknown>(url: string, opts?: RequestOptions): Promise<ApiResponse<T>>
  post<T = unknown>(url: string, body?: unknown, opts?: RequestOptions): Promise<ApiResponse<T>>
  put<T = unknown>(url: string, body?: unknown, opts?: RequestOptions): Promise<ApiResponse<T>>
  patch<T = unknown>(url: string, body?: unknown, opts?: RequestOptions): Promise<ApiResponse<T>>
  delete<T = unknown>(url: string, opts?: RequestOptions): Promise<ApiResponse<T>>
}

const api: ApiClient = {
  get:    (url, opts)       => request('GET',    url, null, opts),
  post:   (url, body, opts) => request('POST',   url, body, opts),
  put:    (url, body, opts) => request('PUT',    url, body, opts),
  patch:  (url, body, opts) => request('PATCH',  url, body, opts),
  delete: (url, opts)       => request('DELETE', url, null, opts),
}

export default api
